import React, { useState, useRef, useEffect } from 'react'
import { BiCalendar, BiX } from 'react-icons/bi'
const DateRangePicker = ({
  onDateRangeChange,
  setStartDate,
  startDate,
  setEndDate,
  endDate
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selecting, setSelecting] = useState('start')
  const datePickerRef = useRef(null)

  useEffect(() => {
    if (startDate) {
      setCurrentMonth(new Date(startDate))
    }
  }, [startDate])

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const formatDate = dateString => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const clearDates = e => {
    e.stopPropagation()
    setStartDate('')
    setEndDate('')
    onDateRangeChange('', '')
    setIsOpen(false)
  }

  const prevMonth = () => {
    setCurrentMonth(prevState => {
      const prevMonthDate = new Date(prevState)
      prevMonthDate.setMonth(prevMonthDate.getMonth() - 1)
      return prevMonthDate
    })
  }

  const nextMonth = () => {
    setCurrentMonth(prevState => {
      const nextMonthDate = new Date(prevState)
      nextMonthDate.setMonth(nextMonthDate.getMonth() + 1)
      return nextMonthDate
    })
  }

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay()
  }

  const handleDateClick = day => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const newDate = new Date(year, month, day).toISOString().split('T')[0]

    if (selecting === 'start') {
      setStartDate(newDate)
      setSelecting('end')

      if (endDate && endDate < newDate) {
        setEndDate('')
      }
    } else {
      if (startDate && newDate < startDate) {
        setEndDate(startDate)
        setStartDate(newDate)
      } else {
        setEndDate(newDate)
      }

      onDateRangeChange(startDate, newDate)
      setSelecting('start')
      setIsOpen(false)
    }
  }

  const isDateInRange = day => {
    if (!startDate || !endDate) return false

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    )
      .toISOString()
      .split('T')[0]
    return date >= startDate && date <= endDate
  }

  const isDateStart = day => {
    if (!startDate) return false

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    )
      .toISOString()
      .split('T')[0]
    return date === startDate
  }

  const isDateEnd = day => {
    if (!endDate) return false

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    )
      .toISOString()
      .split('T')[0]
    return date === endDate
  }

  const renderCalendar = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    const firstDayOfMonth = getFirstDayOfMonth(year, month)

    const monthName = currentMonth.toLocaleDateString('en-US', {
      month: 'long'
    })

    const daysBefore = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

    const days = []

    const prevMonthDays = getDaysInMonth(year, month - 1)
    for (let i = prevMonthDays - daysBefore + 1; i <= prevMonthDays; i++) {
      days.push({ day: i, currentMonth: false })
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, currentMonth: true })
    }
    const totalDaysShown = Math.ceil((daysInMonth + daysBefore) / 7) * 7
    const daysAfter = totalDaysShown - (daysInMonth + daysBefore)
    for (let i = 1; i <= daysAfter; i++) {
      days.push({ day: i, currentMonth: false })
    }

    const weeks = []
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7))
    }

    return (
      <div className='bg-white min-w-60  rounded-lg shadow-lg p-4'>
        <div className='flex justify-between items-center mb-4'>
          <button
            className='p-1 rounded-full hover:bg-gray-100'
            onClick={prevMonth}
            aria-label='Previous month'
          >
            <svg
              className='h-6 w-6 text-gray-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
          </button>

          <h2 className='text-sm font-bold'>
            {monthName} <span className='text-gray-700'>{year}</span>
          </h2>

          <button
            className='p-1 rounded-full hover:bg-gray-100'
            onClick={nextMonth}
            aria-label='Next month'
          >
            <svg
              className='h-6 w-6 text-gray-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M9 5l7 7-7 7'
              />
            </svg>
          </button>
        </div>

        <div className='grid grid-cols-7 gap-1 mb-2 text-black'>
          <div className='text-center text-sm font-medium '>Mo</div>
          <div className='text-center text-sm font-medium '>Tu</div>
          <div className='text-center text-sm font-medium '>We</div>
          <div className='text-center text-sm font-medium '>Th</div>
          <div className='text-center text-sm font-medium '>Fr</div>
          <div className='text-center text-sm font-medium '>Sa</div>
          <div className='text-center text-sm font-medium '>Su</div>
        </div>

        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className='grid grid-cols-7 gap-1 mb-1'>
            {week.map((day, dayIndex) => {
              const isStart = day.currentMonth && isDateStart(day.day)
              const isEnd = day.currentMonth && isDateEnd(day.day)
              const isInRange = day.currentMonth && isDateInRange(day.day)

              return (
                <button
                  key={dayIndex}
                  onClick={() => day.currentMonth && handleDateClick(day.day)}
                  className={`
                    p-1 flex items-center justify-center rounded-md text-xs shadow shadow-gray-300
                    ${!day.currentMonth ? 'text-gray-300' : 'text-gray-700'}
                    ${isStart ? 'bg-blue-600 text-white' : ''}
                    ${isEnd ? 'bg-blue-600 text-white' : ''}
                    ${isInRange && !isStart && !isEnd ? 'bg-blue-100' : ''}
                    ${
                      day.currentMonth && !isInRange && !isStart && !isEnd
                        ? 'hover:bg-gray-100'
                        : ''
                    }
                  `}
                >
                  {day.day}
                </button>
              )
            })}
          </div>
        ))}

        <div className=' border-t flex justify-end border-gray-200  items-center'>
          <button
            onClick={clearDates}
            className='px-4 text-sm font-medium text-gray-700 hover:text-gray-900 
              transition-colors rounded-md hover:bg-gray-100'
          >
            Clear
          </button>
          <div className='text-sm text-gray-600'>
            {startDate && (
              <span>
                {formatDate(startDate)}
                {endDate && ` - ${formatDate(endDate)}`}
              </span>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='relative w-full max-w-md' ref={datePickerRef}>
      <div
        className={`w-full bg-white rounded-lg shadow-sm border 
        transition-all duration-200 ease-in-out cursor-pointer p-1
        ${
          isOpen
            ? 'ring-2 ring-blue-500 border-blue-500'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className='flex items-center space-x-3 p-2'>
          <div className=''>
            <BiCalendar className='h-5 w-5 text-[#3F4555]' />
          </div>

          <div className='flex flex-col'>
            {/* <span className='text-sm font-medium text-[#0F172ACC]'>
              Sort by Date
            </span> */}
            <div className='flex items-center space-x-2'>
              {startDate && endDate ? (
                <>
                  <span className='text-sm font-medium text-gray-900'>
                    {formatDate(startDate)} - {formatDate(endDate)}
                  </span>
                  <button
                    onClick={clearDates}
                    className='p-1  hover:bg-gray-100 rounded-full transition-all duration-200'
                    aria-label='Clear dates'
                  >
                    <BiX className='h-4 w-4 text-gray-500 hover:text-gray-700' />
                  </button>
                </>
              ) : (
                <span className='text-sm font-medium text-[#0F172ACC]'>
                  Sort by Date
                </span>
              )}
            </div>
          </div>
        </div>
        
      </div>

      {isOpen && (
        <div
          className='absolute top-full right-0 md:right-16 mt-1 w-full bg-white rounded-lg shadow-lg 
          border border-gray-200 z-50 transform transition-all duration-200 ease-out'
        >
          {renderCalendar()}
        </div>
      )}
    </div>
  )
}

export default DateRangePicker
